import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ArticleWithTopic {
  id: string;
  title: string;
  slug: string;
  cover_image: string | null;
  topic_id: string | null;
  topic_name: string | null;
  topic_color: string | null;
}

export function useFeaturedArticles() {
  return useQuery({
    queryKey: ['featured-articles-carousel'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          id,
          title,
          slug,
          cover_image,
          topic_id,
          topics (name, tag_color)
        `)
        .eq('status', 'published')
        .eq('is_featured', true)
        .order('display_order', { ascending: true });

      if (error) throw error;

      return (data || []).map((post: any) => ({
        id: post.id,
        title: post.title,
        slug: post.slug,
        cover_image: post.cover_image,
        topic_id: post.topic_id,
        topic_name: post.topics?.name || null,
        topic_color: post.topics?.tag_color || null,
      })) as ArticleWithTopic[];
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

export function useArticlesByTopic(topicId: string | null) {
  return useQuery({
    queryKey: ['articles-by-topic', topicId],
    queryFn: async () => {
      if (!topicId) return [];

      const { data, error } = await supabase
        .from('posts')
        .select(`
          id,
          title,
          slug,
          cover_image,
          topic_id,
          topics (name, tag_color)
        `)
        .eq('status', 'published')
        .eq('topic_id', topicId)
        .order('published_at', { ascending: false, nullsFirst: false });

      if (error) throw error;

      return (data || []).map((post: any) => ({
        id: post.id,
        title: post.title,
        slug: post.slug,
        cover_image: post.cover_image,
        topic_id: post.topic_id,
        topic_name: post.topics?.name || null,
        topic_color: post.topics?.tag_color || null,
      })) as ArticleWithTopic[];
    },
    enabled: !!topicId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

export function useAllArticles() {
  return useQuery({
    queryKey: ['all-articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          id,
          title,
          slug,
          cover_image,
          topic_id,
          topics (name, tag_color)
        `)
        .eq('status', 'published')
        .order('published_at', { ascending: false, nullsFirst: false });

      if (error) throw error;

      return (data || []).map((post: any) => ({
        id: post.id,
        title: post.title,
        slug: post.slug,
        cover_image: post.cover_image,
        topic_id: post.topic_id,
        topic_name: post.topics?.name || null,
        topic_color: post.topics?.tag_color || null,
      })) as ArticleWithTopic[];
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

// Fetches all articles grouped by topic for efficient section rendering
export function useArticlesGroupedByTopic() {
  return useQuery({
    queryKey: ['articles-grouped-by-topic'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          id,
          title,
          slug,
          cover_image,
          topic_id,
          topics (name, tag_color)
        `)
        .eq('status', 'published')
        .order('published_at', { ascending: false, nullsFirst: false });

      if (error) throw error;

      // Group articles by topic_id
      const grouped: Record<string, ArticleWithTopic[]> = {};
      (data || []).forEach((post: any) => {
        if (post.topic_id) {
          if (!grouped[post.topic_id]) {
            grouped[post.topic_id] = [];
          }
          grouped[post.topic_id].push({
            id: post.id,
            title: post.title,
            slug: post.slug,
            cover_image: post.cover_image,
            topic_id: post.topic_id,
            topic_name: post.topics?.name || null,
            topic_color: post.topics?.tag_color || null,
          });
        }
      });

      return grouped;
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}
